import { h } from '@omni-torn/ui';
import { FACTION_GROWTH_TERMS, growthOver, growthRanks, isLiveView, noGrowthFor, pushLine, readRankedWarList, type RankedWarInfo, SHARE_KEYS, type GrowthPoint, SHARE_LABELS, TERMS_AGREE_NOTE, TERMS_CHANGED_LINK, TERMS_CHANGED_TEXT, TERMS_VERSION, termsAgreed, type LiveView } from '@omni-torn/shared';
import { readFaction, type FactionMember } from '../read';
import { loadMe, server, session, torn } from '../session';
import { shareNow } from '../sync';
import { box, duration, ext, facts, lineChart, loading, num, problem, profileUrl, short, signed, table, tabs, tct, ago } from '../ui';
import { warRoomBusy, warSection, warTick } from './faction-war';
import { armoryHistory, bankSection, chainsSection, raidsSection, resetOps } from './faction-ops';
import { applicationsSection, resetRoster, rosterSection } from './faction-roster';
import { resetWars, warsSection } from './faction-wars';
import { membersActivityBox } from './activity-view';
import { discordSection, resetDiscord } from './faction-discord';
import { delegatesSection, resetDelegates } from './faction-delegates';

interface Shared {
  id: number;
  name: string;
  at: number;
  bars?: Partial<Record<'energy' | 'nerve' | 'happy' | 'life', { current: number; maximum: number; fullAt: number | null }>>;
  cooldowns?: { drug: number | null; booster: number | null; medical: number | null };
  refills?: { energy: boolean | null; nerve: boolean | null };
  travel?: { destination: string; departedAt: number | null; arrivalAt: number | null } | null;
  status?: { state: string; description: string; until: number | null };
  stats?: number | null;
}

const left = (t: number | null | undefined, now: number) => (t && t > now ? duration((t - now) / 1000) : '');
const cd = (t: number | null | undefined, now: number) => {
  const l = left(t, now);
  return l ? h('span', { title: `${tct(t!)} TCT` }, l) : h('span', { class: 't-green' }, '✓');
};

type LiveAnswer = { live: LiveView | { factionId: number; pending: true }; shared: Shared[] };
type Section = 'overview' | 'war' | 'wars' | 'members' | 'roster' | 'applications' | 'activity' | 'growth' | 'oc' | 'armory' | 'chains' | 'bank' | 'raids' | 'discord' | 'delegates';
const SECTIONS: Array<{ id: Section; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'war', label: 'War' },
  // Finished ranked wars member by member, payouts, the ledger across wars (website roadmap S21, faction-wars.ts).
  { id: 'wars', label: 'Wars' },
  { id: 'members', label: 'Members' },
  // Faction ops II (website roadmap S20, faction-roster.ts): the roster (days, estimates, 30-day activity) and applications.
  { id: 'roster', label: 'Roster' },
  { id: 'applications', label: 'Applications' },
  // Activity (website roadmap S22, activity-view.ts): members' 30 days and the faction's heatmap.
  { id: 'activity', label: 'Activity' },
  { id: 'growth', label: 'Growth' },
  { id: 'oc', label: 'Crimes' },
  { id: 'armory', label: 'Armory' },
  // Faction ops I (website roadmap S19, faction-ops.ts): armory history (under Armory), chains, the bank, raids & territory.
  { id: 'chains', label: 'Chains' },
  { id: 'bank', label: 'Bank' },
  { id: 'raids', label: 'Raids' },
  // The Discord servers that get the faction's data (public bot C2/C4, faction-discord.ts): leaders see, remove, pick home.
  { id: 'discord', label: 'Discord' },
  // Faction → Delegates (faction-delegates.ts): the leader and co-leader tick what each member may do.
  { id: 'delegates', label: 'Delegates' },
];

/** A countdown that the page keeps ticking (data-until, ms). */
const countdown = (untilMs: number, cls = '') => h('span', { class: `cd ${cls}`.trim(), 'data-until': String(untilMs), title: `${tct(untilMs)} TCT` }, duration((untilMs - Date.now()) / 1000));
const attackUrl = (id: number) => `https://www.torn.com/page.php?sid=attack&user2ID=${id}`;

export async function renderFaction(view: HTMLElement): Promise<void> {
  resetOps();
  resetRoster();
  resetWars();
  resetDiscord();
  resetDelegates();
  await loadMe().catch(() => null);
  const body = h('div', {}, loading('Reading your faction…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Faction'), h('p', { class: 'muted' }, 'Your faction as Omni-Torn sees it: the Discord bot’s watch (war, enemy, pushes, crimes, armory) and what members share.')), body);
  const me = session.me();
  if (!me?.factionId) {
    body.replaceChildren(sharingBox(), h('p', { class: 'notice' }, 'You weren’t in a faction when you signed in — sign out and in again.'));
    return;
  }
  let fac: ReturnType<typeof readFaction> = null;
  let wars: RankedWarInfo[] = [];
  let readAt = 0;
  let liveAnswer: LiveAnswer | null = null;
  let liveProblem = '';
  const readLive = () =>
    server<LiveAnswer>('GET', '/api/faction/live')
      .then((a) => {
        liveAnswer = a;
        liveProblem = '';
      })
      .catch((err: Error) => {
        liveProblem = err.message;
      });
  try {
    // Its ranked wars in the same call (the War tab).
    const [f] = await Promise.all([torn('/faction/', { selections: 'basic,rankedwars' }, 60_000), readLive()]);
    fac = readFaction(f);
    wars = readRankedWarList(f);
    readAt = Date.now();
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  if (!fac) {
    body.replaceChildren(h('p', { class: 'muted' }, 'Torn didn’t send your faction.'));
    return;
  }
  const f = fac;
  (view.querySelector('.page-head h1') as HTMLElement).textContent = `${f.name}${f.tag ? ` [${f.tag}]` : ''}`;
  // A shared payout link (/faction?war=…#wars) opens the Wars tab, even when sign-in dropped the #.
  let section: Section = (SECTIONS.find((x) => `#${x.id}` === location.hash)?.id ?? (/[?&]war=\d/.test(location.search) ? 'wars' : 'overview')) as Section;
  const content = h('div', {});
  const status = h('p', { class: 'muted small', 'data-live': 'status' });
  // Members' names by id (Torn's member list, with the bot's newer one), for the faction ops tabs.
  const names = () => new Map(freshMembers(f, liveAnswer && isLiveView(liveAnswer.live) ? liveAnswer.live : null, readAt).map((m) => [m.id, m.name] as const));
  const draw = () => {
    const live = liveAnswer && isLiveView(liveAnswer.live) ? liveAnswer.live : null;
    const pending = !!liveAnswer && !live;
    const shared = liveAnswer?.shared ?? [];
    const reads = [live?.enemyAt ? `enemy read ${ago(live.enemyAt * 1000)}` : '', live?.membersAt ? `members ${ago(live.membersAt * 1000)}` : ''].filter(Boolean).join(', ');
    status.textContent = live
      ? `The Discord bot’s watch${reads ? ` (${reads})` : ''}${live.discord ? ' — the same as your faction’s Discord alerts' : ''}. This page refreshes by itself.`
      : pending
        ? 'The Discord bot is taking its first look at your faction — this fills in within a minute.'
        : liveProblem
          ? `The faction watch isn’t available: ${liveProblem}`
          : '';
    const parts: Record<Section, () => Node[]> = {
      overview: () => overview(live, f, shared, readAt),
      wars: () => warsSection(),
      war: () => warSection(live, freshMembers(f, live, readAt), f.id ?? me.factionId!, wars, () => section === 'war' && draw()),
      members: () => membersSection(f, shared, live, readAt),
      roster: () => [rosterSection(() => freshMembers(f, liveAnswer && isLiveView(liveAnswer.live) ? liveAnswer.live : null, readAt), f.id ?? me.factionId!)],
      applications: () => [applicationsSection()],
      growth: () => growthSection(f),
      activity: () => [activitySection(names(), f.id ?? me.factionId!)],
      oc: () => ocSection(live),
      armory: () => [...armorySection(live), armoryHistory(names())],
      chains: () => [chainsSection(names())],
      bank: () => [bankSection(names(), { id: me.playerId, name: me.name })],
      raids: () => [raidsSection(f.id ?? me.factionId!)],
      discord: () => [discordSection()],
      delegates: () => [delegatesSection(() => freshMembers(f, liveAnswer && isLiveView(liveAnswer.live) ? liveAnswer.live : null, readAt))],
    };
    content.replaceChildren(...(live?.problem ? [h('p', { class: 'notice' }, live.problem)] : []), ...parts[section]());
  };
  draw();
  body.replaceChildren(
    tabs(SECTIONS, section, (id) => {
      section = id as Section;
      history.replaceState(null, '', `/faction#${id}`);
      draw();
    }),
    status,
    content,
  );
  // Keep it live while it's open: the watch every 20 s, countdowns every second (and the war room's own reads, if chosen).
  let n = 0;
  const timer = setInterval(() => {
    if (!view.isConnected || !body.isConnected) return clearInterval(timer);
    for (const el of body.querySelectorAll<HTMLElement>('.cd[data-until]')) {
      const left = (Number(el.dataset.until) - Date.now()) / 1000;
      el.textContent = left > 0 ? duration(left) : 'now';
    }
    // A form field of the war room in use: its redraw waits (the next one draws).
    if (++n % 20 === 0 && document.visibilityState === 'visible') void readLive().then(() => !warRoomBusy() && draw());
    // The war room follows the enemy with your key only while the War tab is shown and the page is in view.
    if (section === 'war') warTick();
  }, 1000);
}

// ---------------------------------------------------------------- the sections

function overview(live: LiveView | null, f: NonNullable<ReturnType<typeof readFaction>>, shared: Shared[], readAt: number): Node[] {
  const now = Date.now() / 1000;
  const out: Node[] = [];
  const members = freshMembers(f, live, readAt);
  const online = members.filter((m) => m.online === 'Online').length;
  const hosp = members.filter((m) => /hospital/i.test(m.state)).length;
  const flying = members.filter((m) => /travel|abroad/i.test(m.state)).length;
  const war = live?.war;
  const us = war?.factions.find((x) => x.id === live!.factionId);
  const them = war?.factions.find((x) => x.id !== live!.factionId);
  out.push(
    box('Right now', [
      facts([
        ['Members', `${members.length} · ${online} online · ${hosp} in hospital · ${flying} flying or abroad · ${shared.length} sharing`],
        ['War', war && us && them ? h('span', {}, `Ranked war against ${them.name}: ${num(us.score)} – ${num(them.score)}${war.target ? ` (target ${num(war.target)})` : ''} · `, war.start > now ? h('span', {}, 'starts in ', countdown(war.start * 1000)) : `running ${duration(now - war.start)}`) : live?.enemy ? `Watching ${live.enemy.name} (set in Discord with /war enemy)` : 'No ranked war'],
        ['Enemy', live?.enemy ? enemySummary(live) : '—'],
        ['Chain', live?.chain ? h('span', {}, `${num(live.chain.current)}${live.chain.max ? ` / ${num(live.chain.max)}` : ''}`, live.chain.timeout > 0 ? h('span', {}, ' · drops in ', countdown((live.chain.at + live.chain.timeout) * 1000, live.chain.timeout < 90 ? 'st-red' : '')) : '') : '—'],
        ['Discord', live ? (live.discord ? 'Your faction’s Discord server gets the bot’s alerts (leaders: the Discord tab lists every server that gets your data).' : 'No Discord server gets alerts yet — add the Omni-Torn bot and run /omni faction there.') : '—'],
      ]),
    ]),
  );
  if (live?.enemy) {
    const line = pushLine(live);
    out.push(
      box('Enemy activity', [
        line ? h('p', { class: live.push?.level === 'push' ? 'notice bad' : live.push?.level === 'rising' ? 'notice' : '', 'data-live': 'push' }, line) : h('p', { class: 'muted' }, 'Not read yet.'),
        live.push?.reasons.length ? h('p', { class: 'muted small' }, live.push.reasons.join(' · ')) : null,
        live.pushes.length ? h('ul', { class: 'plain-list' }, ...[...live.pushes].reverse().slice(0, 6).map((p) => h('li', {}, `${tct(p.at * 1000)} TCT — ${p.level === 'push' ? 'Push' : p.level === 'rising' ? 'Getting busy' : 'Calmed down'}: ${p.online} online${p.usual !== null ? ` (usually ${Math.round(p.usual)})` : ''}`))) : null,
      ]),
    );
  }
  if (live?.retals.length) {
    out.push(box('Retals open', h('ul', { class: 'plain-list' }, ...live.retals.map((r) => h('li', {}, r.attacker.id ? ext(attackUrl(r.attacker.id), r.attacker.name ?? `#${r.attacker.id}`) : 'Someone', ` hit ${r.defender ?? 'us'} — `, countdown(r.until * 1000), ' left')))));
  }
  return out;
}

function enemySummary(live: LiveView): Node {
  const m = live.enemyMembers;
  const online = m.filter((x) => x.activity === 'online').length;
  const hosp = m.filter((x) => x.state === 'Hospital').length;
  const ready = m.filter((x) => x.state === 'Okay').length;
  const flying = live.trips.filter((t) => t.leg !== 'there').length;
  return h('span', {}, `${live.enemy!.name}: ${m.length} members · ${online} online · ${ready} okay · ${hosp} in hospital · ${flying} flying`);
}

/** The Members sub-tab picked (kept while the page refreshes). */
let membersWhich: 'all' | 'shared' | 'flying' | 'hospital' | 'online' = 'all';

/** Torn's member list (read when the page opened), with the bot's newer statuses when it has them. */
function freshMembers(f: NonNullable<ReturnType<typeof readFaction>>, live: LiveView | null, readAt: number): FactionMember[] {
  if (!live?.membersAt || live.membersAt * 1000 <= readAt) return f.members;
  const bot = new Map(live.members.map((m) => [m.id, m]));
  return f.members.map((m) => {
    const b = bot.get(m.id);
    if (!b) return m;
    const color = b.state === 'Hospital' || b.state === 'Jail' || b.state === 'Federal' ? 'red' : b.state === 'Traveling' || b.state === 'Abroad' ? 'blue' : 'green';
    return { ...m, state: b.state, description: b.description, until: b.until ? b.until * 1000 : null, color, online: b.activity === 'online' ? 'Online' : b.activity === 'idle' ? 'Idle' : 'Offline' };
  });
}

function membersSection(f: NonNullable<ReturnType<typeof readFaction>>, shared: Shared[], live: LiveView | null, readAt: number): Node[] {
  const byId = new Map(shared.map((s) => [s.id, s]));
  const lands = new Map((live?.members ?? []).filter((m) => m.landsAt).map((m) => [m.id, m.landsAt!]));
  type Row = FactionMember & { share: Shared | undefined };
  const rows: Row[] = freshMembers(f, live, readAt).map((m) => ({ ...m, share: byId.get(m.id) }));
  const now = Date.now();
  let which = membersWhich;
  const holder = h('div', {});
  const draw = () => {
    const list = rows.filter((r) => which === 'all' || (which === 'shared' ? !!r.share : which === 'flying' ? /travel|abroad/i.test(r.state) : which === 'hospital' ? /hospital/i.test(r.state) : r.online === 'Online'));
    holder.replaceChildren(
      table(
        list,
        [
          { label: 'Member', get: (r) => h('span', {}, ext(profileUrl(r.id), r.name), h('span', { class: 'muted small' }, ` ${r.position}`)), sort: (r) => r.name.toLowerCase() },
          { label: 'Lvl', get: (r) => String(r.level), sort: (r) => r.level, cls: 'r' },
          { label: 'Status', get: (r) => h('span', { class: `st-${r.color}` }, r.description || r.state, r.until && r.until > now ? h('span', { class: 'muted' }, ' · ', countdown(r.until)) : null), sort: (r) => r.state },
          { label: 'Seen', get: (r) => h('span', { class: `dot dot-${r.online.toLowerCase()}`, title: r.online }, r.lastAction), sort: (r) => -(r.lastActionAt ?? 0) },
          { label: 'Drug', get: (r) => (r.share?.cooldowns ? cd(r.share.cooldowns.drug, now) : '·'), sort: (r) => r.share?.cooldowns?.drug ?? (r.share?.cooldowns ? 0 : 9e15), cls: 'c' },
          { label: 'Booster', get: (r) => (r.share?.cooldowns ? cd(r.share.cooldowns.booster, now) : '·'), sort: (r) => r.share?.cooldowns?.booster ?? (r.share?.cooldowns ? 0 : 9e15), cls: 'c' },
          { label: 'Medical', get: (r) => (r.share?.cooldowns ? cd(r.share.cooldowns.medical, now) : '·'), sort: (r) => r.share?.cooldowns?.medical ?? (r.share?.cooldowns ? 0 : 9e15), cls: 'c' },
          { label: 'Energy', get: (r) => (r.share?.bars?.energy ? `${r.share.bars.energy.current}/${r.share.bars.energy.maximum}` : '·'), sort: (r) => r.share?.bars?.energy?.current ?? -1, cls: 'r' },
          { label: 'Refill', get: (r) => (r.share?.refills ? (r.share.refills.energy ? h('span', { class: 't-green', title: 'Energy refill not used today' }, 'E') : '—') : '·'), sort: (r) => (r.share?.refills?.energy ? 1 : 0), cls: 'c' },
          { label: 'Flight', get: (r) => (r.share?.travel ? `${r.share.travel.destination}${r.share.travel.arrivalAt ? ` · lands ${tct(r.share.travel.arrivalAt)}` : ''}` : lands.get(r.id) ? `lands ${tct(lands.get(r.id)! * 1000)}` : '·'), sort: (r) => r.share?.travel?.arrivalAt ?? 9e15 },
          { label: 'Stats', get: (r) => (r.share?.stats ? short(r.share.stats) : '·'), sort: (r) => r.share?.stats ?? -1, cls: 'r' },
          { label: 'Shared', get: (r) => (r.share ? h('span', { class: 'muted small' }, ago(r.share.at)) : '·'), sort: (r) => -(r.share?.at ?? 0) },
        ],
        { initial: 3, desc: false, empty: 'No one here.', remember: 'faction-members' },
      ),
    );
  };
  draw();
  const flying = rows.filter((r) => /travel|abroad/i.test(r.state)).length;
  const online = rows.filter((r) => r.online === 'Online').length;
  const hosp = rows.filter((r) => /hospital/i.test(r.state)).length;
  return [
    box(
      `Members (${rows.length})`,
      [
        h('p', { class: 'muted' }, `Cooldowns, energy, refills and flights show for members who share them (· = not shared).`),
        tabs(
          [
            { id: 'all', label: 'All' },
            { id: 'shared', label: `Sharing (${shared.length})` },
            { id: 'online', label: `Online (${online})` },
            { id: 'flying', label: `Flying (${flying})` },
            { id: 'hospital', label: `Hospital (${hosp})` },
          ],
          which,
          (id) => {
            which = membersWhich = id as typeof which;
            draw();
          },
        ),
        holder,
      ],
      { extra: f.id ? ext(`https://www.torn.com/factions.php?step=profile&ID=${f.id}`, 'Faction page ↗') : null },
    ),
    sharingBox(),
  ];
}

// ---------------------------------------------------------------- members' activity (website roadmap S22)

/** Kept while the page is open (the section redraws every 20 s with the bot's watch); asked again after 5 minutes. */
let activityCache: { at: number; faction: number; node: HTMLElement } | null = null;
function activitySection(names: ReadonlyMap<number, string>, factionId: number): HTMLElement {
  if (activityCache && activityCache.faction === factionId && Date.now() - activityCache.at < 5 * 60_000) return activityCache.node;
  const node = h('div', { 'data-faction-section': 'activity' }, loading());
  activityCache = { at: Date.now(), faction: factionId, node };
  void membersActivityBox(names, factionId).then((parts) => node.replaceChildren(...parts));
  return node;
}

// ---------------------------------------------------------------- members' growth (who's skipping training?)

interface GrowthAnswer {
  terms: number;
  keepDays: number;
  members: Array<{ id: number; name: string; at: number; points: GrowthPoint[] }>;
  latest: Array<{ id: number; name: string; total: number; at: number }>;
}
const GROWTH_SPANS = [
  { id: '7', label: '7 days' },
  { id: '14', label: '14 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
];
let growthSpan = '30';
/** The answer is kept while the page is open (the section redraws every 20 s with the bot's watch). */
let growthCache: { at: number; faction: number; node: HTMLElement } | null = null;

/**
 * Each sharing member's battle stats TOTAL over time (terms v2 share the total only — never single stats), its growth
 * over a span, a rank, and a flag when it didn't grow for 7 / 14 / 30 days. Only members of this faction see it.
 */
function growthSection(f: NonNullable<ReturnType<typeof readFaction>>): Node[] {
  // The same box again while it's fresh (the page redraws every 20 s): an open chart and the span stay.
  if (growthCache && growthCache.faction === f.id && Date.now() - growthCache.at < 5 * 60_000) return [growthCache.node];
  const holder = h('div', { 'data-growth': 'body' }, loading('Reading members’ growth…'));
  const node = box('Members’ growth', holder, { id: 'growth' });
  if (f.id !== null) growthCache = { at: Date.now(), faction: f.id, node };
  server<GrowthAnswer>('GET', '/api/faction/growth')
    .then((a) => holder.replaceChildren(...growthBody(a, f)))
    .catch((err) => {
      growthCache = null;
      holder.replaceChildren(problem(err));
    });
  return [node];
}

function growthBody(a: GrowthAnswer, f: NonNullable<ReturnType<typeof readFaction>>): Node[] {
  const now = Date.now();
  const inFaction = new Map(f.members.map((m) => [m.id, m]));
  type Row = { id: number; name: string; total: number | null; at: number; points: GrowthPoint[] };
  const rows = new Map<number, Row>();
  for (const m of a.members) rows.set(m.id, { id: m.id, name: m.name, total: m.points[m.points.length - 1]?.total ?? null, at: m.at, points: m.points });
  for (const l of a.latest) {
    const r = rows.get(l.id);
    if (!r) rows.set(l.id, { id: l.id, name: l.name, total: l.total, at: l.at, points: [] });
    else if (l.at >= r.at) Object.assign(r, { total: l.total, at: l.at });
  }
  // Only who's in the faction now (a member who left keeps nothing here).
  const list = [...rows.values()].filter((r) => !inFaction.size || inFaction.has(r.id)).map((r) => ({ ...r, name: inFaction.get(r.id)?.name ?? r.name }));
  const withHistory = list.filter((r) => r.points.length);
  const out: Node[] = [];
  out.push(
    h(
      'p',
      { class: 'muted small' },
      'Battle stats totals that members share with the faction (never single stats). ',
      FACTION_GROWTH_TERMS > TERMS_VERSION
        ? `Totals over time are kept (a day each, ${a.keepDays} days) for members who agree to the next terms update — until then this shows each member’s latest total.`
        : `Totals over time are kept (a day each, ${a.keepDays} days) for members who agreed to Omni-Torn’s terms version ${FACTION_GROWTH_TERMS} or later.`,
    ),
  );
  const chart = h('div', { 'data-growth': 'chart' });
  const showChart = (r: Row) => chart.replaceChildren(box(`${r.name}: battle stats total`, lineChart(r.points.map((p) => ({ at: p.day, v: p.total })), { label: `${r.name} total` }), { extra: h('button', { type: 'button', class: 'btn-small', onclick: () => chart.replaceChildren() }, 'Close') }));
  const tableHolder = h('div', {});
  const draw = () => {
    const days = Number(growthSpan);
    const gains = list.map((r) => ({ id: r.id, gain: growthOver(r.points, days, now) }));
    const gainOf = new Map(gains.map((g) => [g.id, g.gain]));
    const ranks = growthRanks(gains);
    const flat = list.filter((r) => noGrowthFor(r.points, now) !== null).length;
    const pct = (r: Row) => {
      const g = gainOf.get(r.id);
      return g === null || g === undefined || !r.total || r.total - g <= 0 ? null : (g / (r.total - g)) * 100;
    };
    tableHolder.replaceChildren(
      h('p', { class: 'muted small', 'data-growth': 'summary' }, `${list.length} sharing their total · ${withHistory.length} with history · ${flat} not growing`),
      table(
        list,
        [
          { label: 'Member', get: (r) => h('span', {}, ext(profileUrl(r.id), r.name), r.points.length > 1 ? h('button', { type: 'button', class: 'btn-small btn-gap', title: 'Their total over time', onclick: () => showChart(r) }, 'Chart') : null), sort: (r) => r.name.toLowerCase() },
          { label: 'Total', get: (r) => h('span', { title: num(r.total) }, short(r.total)), sort: (r) => r.total ?? -1, cls: 'r' },
          { label: `Gain (${GROWTH_SPANS.find((s) => s.id === growthSpan)!.label})`, get: (r) => signed(gainOf.get(r.id) ?? null, short), sort: (r) => gainOf.get(r.id) ?? -1e18, cls: 'r' },
          { label: '%', get: (r) => (pct(r) === null ? '—' : `${num(pct(r), 1)}%`), sort: (r) => pct(r) ?? -1e9, cls: 'r' },
          { label: 'Rank', get: (r) => (ranks.get(r.id) ? `#${ranks.get(r.id)}` : '—'), sort: (r) => ranks.get(r.id) ?? 1e6, cls: 'r' },
          { label: 'Flag', get: (r) => { const d = noGrowthFor(r.points, now); return d ? h('span', { class: 'flag-warn', 'data-flag': String(d) }, `⚠ no growth in ${d} days`) : r.points.length ? '' : h('span', { class: 'muted small' }, 'no history yet'); }, sort: (r) => noGrowthFor(r.points, now) ?? (r.points.length ? 0 : -1) },
          { label: 'Shared', get: (r) => h('span', { class: 'muted small' }, ago(r.at)), sort: (r) => -r.at },
        ],
        { initial: 4, desc: false, empty: 'No member shares their battle stats total yet (it needs a Limited key).', remember: 'faction-growth' },
      ),
    );
  };
  draw();
  out.push(
    tabs(GROWTH_SPANS, growthSpan, (id) => {
      growthSpan = id;
      draw();
    }),
    tableHolder,
    chart,
  );
  return out;
}

function ocSection(live: LiveView | null): Node[] {
  const oc = live?.oc;
  if (!oc) return [h('p', { class: 'muted' }, live ? 'The bot hasn’t read your faction’s crimes (it needs a key with the faction’s API access: a member gives the bot one in the extension’s options).' : 'Not available yet.')];
  const now = Date.now() / 1000;
  const crimes = [...oc.crimes].sort((a, b) => (a.readyAt ?? 9e15) - (b.readyAt ?? 9e15));
  return [
    box(`Crimes being planned (${crimes.length})`, [
      h('p', { class: 'muted small' }, `Read ${ago(oc.at * 1000)}.`),
      ...crimes.map((c) =>
        h(
          'div',
          { class: 'oc-crime' },
          h('h3', {}, c.name, c.difficulty ? h('span', { class: 'muted small' }, ` level ${c.difficulty}`) : null, ' — ', c.readyAt ? (c.readyAt <= now ? h('span', { class: 'st-red' }, 'held up past ready') : h('span', {}, 'ready in ', countdown(c.readyAt * 1000))) : c.status),
          h(
            'ul',
            { class: 'plain-list' },
            ...c.slots.map((s) =>
              h(
                'li',
                {},
                h('b', {}, s.position), ': ',
                s.userId ? ext(profileUrl(s.userId), s.name ?? `#${s.userId}`) : h('span', { class: 'st-red' }, 'empty'),
                s.passRate !== null ? h('span', { class: s.passRate < 60 ? 'st-red' : 'muted' }, ` · ${s.passRate}%`) : null,
                s.itemId && s.itemAvailable === false ? h('span', { class: 'st-red' }, ' · missing their item') : null,
              ),
            ),
          ),
        ),
      ),
      crimes.length ? null : h('p', { class: 'muted' }, 'None.'),
    ]),
    box(`Not in a crime (${oc.out.length})`, oc.out.length ? h('ul', { class: 'plain-list' }, ...oc.out.map((o) => h('li', {}, ext(profileUrl(o.id), o.name), h('span', { class: 'muted' }, ` — for ${duration(now - o.since)}`)))) : h('p', { class: 'muted' }, 'Everyone is in one.')),
  ];
}

function armorySection(live: LiveView | null): Node[] {
  const a = live?.armory;
  if (!a) return [h('p', { class: 'muted' }, live ? 'Nothing kept stocked yet: in Discord, a bot admin picks items with /armory track (the bot checks every 4 hours and posts when one runs low).' : 'Not available yet.')];
  return [
    box('Kept stocked', [
      h('p', { class: 'muted small' }, `Checked ${ago(a.at * 1000)} · items your Discord server’s bot watches (/armory track).`),
      table(
        a.items,
        [
          { label: 'Item', get: (i) => h('span', { class: i.low ? 'st-red' : '' }, i.low ? '⚠ ' : '', i.name), sort: (i) => i.name },
          { label: 'In the armory', get: (i) => num(i.available), sort: (i) => i.available, cls: 'r' },
          { label: 'Loaned out', get: (i) => num(Math.max(0, i.quantity - i.available)), sort: (i) => i.quantity - i.available, cls: 'r' },
          { label: 'Wanted', get: (i) => (i.min !== null ? num(i.min) : '—'), sort: (i) => i.min ?? 0, cls: 'r' },
        ],
        { initial: 0, desc: false, remember: 'faction-armory' },
      ),
    ]),
  ];
}

/**
 * Your own sharing. Since terms version 2 (v0.104.0) it's part of using Omni-Torn: everything listed goes to
 * your faction, no switch. Until the player's agreement to the current terms is recorded (the "terms changed" banner
 * above every page — continuing to use Omni-Torn agrees, main.ts), the box says so instead of offering "Share now".
 */
export function sharingBox(): HTMLElement {
  const me = session.me();
  const status = h('span', { class: 'save-status', role: 'status' });
  const always = h('p', {}, 'Always shared (part of using Omni-Torn): ', SHARE_KEYS.map((k) => SHARE_LABELS[k]).join(', '), '.');
  if (me && !termsAgreed(me.prefs)) {
    return box(
      'What you share with your faction',
      [
        always,
        h('p', { class: 'notice', 'data-share': 'terms' }, TERMS_CHANGED_TEXT, h('a', { href: '/terms', target: '_blank', rel: 'noopener' }, TERMS_CHANGED_LINK), '. ', TERMS_AGREE_NOTE),
      ],
      { id: 'sharing' },
    );
  }
  const now = h('button', { type: 'button' }, 'Share now');
  now.addEventListener('click', async () => {
    now.disabled = true;
    status.className = 'save-status';
    status.textContent = 'Sharing…';
    const r = await shareNow().catch((e: Error) => ({ ok: false, message: e.message }));
    status.className = `save-status ${r.ok ? 'ok' : 'bad'}`;
    status.textContent = r.ok ? 'Shared just now.' : r.message;
    now.disabled = false;
  });
  return box(
    'What you share with your faction',
    [
      always,
      me?.factionId ? h('div', { class: 'ask-buttons' }, now, status) : null,
      h('p', { class: 'muted small' }, 'Only faction mates signed in to Omni-Torn see it, and only while you’re both in the faction. The extension shares the same, every few minutes.'),
    ],
    { id: 'sharing' },
  );
}
